import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { CircularProgress, Box } from '@mui/material';
import PasswordChangeModal from './PasswordChangeModal';
import AgencyTermsDialog from './AgencyTermsDialog';
import agencyService from '../services/agencyService';
import { acceptAgencyTerms, hasAcceptedAgencyTerms } from '../utils/agencyTermsAcceptance';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, user, employee, userType, updateUser, setEmployee, logout } = useAuthStore();
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [termsDialogOpen, setTermsDialogOpen] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const termsPending = userType === 'agency' && !!user?._id && !hasAcceptedAgencyTerms(user);
  const passwordPromptKey = `password_change_prompted_${user?._id || employee?.id || 'guest'}`;
  const hasSeenPasswordPrompt = typeof window !== 'undefined' && sessionStorage.getItem(passwordPromptKey) === 'true';

  useEffect(() => {
    if (termsPending) {
      setTermsDialogOpen(true);
      setTermsAccepted(false);
      return;
    }

    setTermsDialogOpen(false);
    setTermsAccepted(false);
  }, [termsPending]);

  useEffect(() => {
    if (!termsPending && ((userType === 'employee' && employee?.mustChangePassword) || (userType === 'agency' && user?.requires_password_change))) {
      if (!hasSeenPasswordPrompt) {
        setPasswordModalOpen(true);
        sessionStorage.setItem(passwordPromptKey, 'true');
      }
      return;
    }

    if (!termsPending) {
      setPasswordModalOpen(false);
    }
  }, [employee?.mustChangePassword, hasSeenPasswordPrompt, passwordPromptKey, termsPending, user?.requires_password_change, userType]);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const handleAcceptTerms = async () => {
    if (!user?._id) {
      return;
    }

    try {
      const response = await agencyService.acceptTerms();
      if (response.success && response.data) {
        acceptAgencyTerms(user._id);
        updateUser(response.data);
      }
      setTermsDialogOpen(false);
      setTermsAccepted(false);
    } catch {
      setTermsAccepted(false);
    }
  };

  const handleRejectTerms = () => {
    setTermsDialogOpen(false);
    setTermsAccepted(false);
    localStorage.setItem('agency_status_notice', "Vous devez accepter les conditions pour acceder au systeme.");
    logout();
    navigate('/login', { replace: true });
  };

  // Force password change if required
  const mustChangePassword = userType === 'employee' 
    ? employee?.mustChangePassword 
    : user?.requires_password_change;

  // Check if already dismissed in this session
  const sessionKey = `password_change_dismissed_${user?._id || employee?.id}`;
  const isDismissedThisSession = typeof window !== 'undefined' && sessionStorage.getItem(sessionKey) === 'true';

  const shouldShowPasswordModal = !termsPending && mustChangePassword && passwordModalOpen && !isDismissedThisSession;

  const handlePasswordChangeSuccess = () => {
    setPasswordModalOpen(false);
    if (userType === 'employee' && employee) {
      setEmployee({ ...employee, mustChangePassword: false });
    } else if (user) {
      updateUser({ requires_password_change: false });
    }
  };

  const handleTryLater = () => {
    // Mark as dismissed for this session to prevent re-prompting
    const sessionKey = `password_change_dismissed_${user?._id || employee?.id}`;
    sessionStorage.setItem(sessionKey, 'true');
    sessionStorage.setItem(passwordPromptKey, 'true');
    setPasswordModalOpen(false);
    // User can access dashboard, but will be reminded on next login
  };

  const handleLogout = () => {
    setPasswordModalOpen(false);
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <>
      {children}
      {shouldShowPasswordModal && (
        <PasswordChangeModal
          open={passwordModalOpen}
          onSuccess={handlePasswordChangeSuccess}
          onTryLater={userType === 'employee' ? handleTryLater : undefined}
          onLogout={handleLogout}
        />
      )}
      <AgencyTermsDialog
        open={termsDialogOpen || termsPending}
        accepted={termsAccepted}
        onAcceptedChange={setTermsAccepted}
        onAccept={handleAcceptTerms}
        onReject={handleRejectTerms}
      />
    </>
  );
};

export default ProtectedRoute;
